import React from 'react';
import { motion } from 'framer-motion';
import { ArrowRight, Code, Zap, Shield } from 'lucide-react';
import { Link } from 'react-scroll';

const Hero = () => {
  return (
    <section id="hero" className="relative min-h-screen flex items-center justify-center overflow-hidden">
      {/* Background Image with Overlay */}
      <div className="absolute inset-0 z-0">
        <img
          src="https://images.unsplash.com/photo-1769375457737-aaa532ad7e94?crop=entropy&cs=srgb&fm=jpg&q=85"
          alt="Abstract Blue Digital Waves"
          className="w-full h-full object-cover"
        />
        <div className="absolute inset-0 bg-gradient-to-b from-slate-900/80 via-slate-900/60 to-slate-900/90"></div>
      </div>

      {/* Content */}
      <div className="relative z-10 max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-32 text-center">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6 }}
        >
          <div className="inline-block mb-6">
            <span className="text-sm font-medium tracking-wide uppercase text-blue-400 bg-blue-500/10 px-4 py-2 rounded-full border border-blue-500/20">
              Software Solutions That Scale
            </span>
          </div>
          
          <h1 className="text-5xl md:text-7xl font-bold tracking-tight leading-tight text-white mb-6">
            Building The Future <br />
            <span className="text-blue-400">One Line of Code</span> at a Time
          </h1>
          
          <p className="text-lg md:text-xl leading-relaxed text-slate-300 max-w-3xl mx-auto mb-12">
            We craft innovative software solutions that transform businesses. From web applications to mobile apps, we turn your vision into reality with cutting-edge technology.
          </p>

          <div className="flex flex-col sm:flex-row gap-4 justify-center items-center">
            <Link
              to="contact"
              smooth={true}
              duration={500}
              offset={-80}
              className="bg-blue-600 text-white h-12 px-8 rounded-full font-medium transition-all duration-300 shadow-lg hover:shadow-blue-600/25 hover:bg-blue-700 flex items-center gap-2 cursor-pointer"
              data-testid="hero-cta-primary"
            >
              Start Your Project <ArrowRight size={20} />
            </Link>
            <Link
              to="portfolio"
              smooth={true}
              duration={500}
              offset={-80}
              className="bg-white text-slate-900 border border-slate-200 hover:bg-slate-50 h-12 px-8 rounded-full font-medium transition-all duration-300 flex items-center gap-2 cursor-pointer"
              data-testid="hero-cta-secondary"
            >
              View Our Work
            </Link>
          </div>
        </motion.div>

        {/* Feature Pills */}
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6, delay: 0.3 }}
          className="mt-20 grid grid-cols-1 md:grid-cols-3 gap-6 max-w-4xl mx-auto"
        >
          {[
            { icon: Code, title: 'Clean Code', desc: 'Maintainable & Scalable' },
            { icon: Zap, title: 'Fast Delivery', desc: 'On Time, Every Time' },
            { icon: Shield, title: 'Secure', desc: 'Enterprise-Grade Security' },
          ].map((feature, index) => (
            <div
              key={index}
              className="bg-white/10 backdrop-blur-md p-6 rounded-2xl border border-white/10 hover:bg-white/15 transition-all duration-300"
              data-testid={`hero-feature-${index}`}
            >
              <feature.icon className="text-blue-400 mb-3" size={32} />
              <h3 className="text-white font-semibold text-lg mb-1">{feature.title}</h3>
              <p className="text-slate-300 text-sm">{feature.desc}</p>
            </div>
          ))}
        </motion.div>
      </div>
    </section>
  );
};

export default Hero;